// 2020, Picsa Advisory Services (Pty) Ltd. All rights reserved.

import React from 'react'
import PropTypes from 'prop-types'
import { graphql, compose } from 'react-apollo'
import gql from 'graphql-tag'
import * as Sentry from '@sentry/browser'
import moment from 'moment'
import DatePicker from 'react-datepicker'

import {
  InfoDisplay,
  Input,
  SpanWithData,
  SelectWithData,
  Spinner,
  Warning,
  SvgIcon
} from '../../'

import {
  isValidSouthAfricanID,
  getDateOfBirthFromSouthAfricanID,
  notifyCoreID
} from '../../../utils'

import { COREID_ACTIVE } from '../../../constants'


const initialiseState = (identity) => {
  let { firstName, otherName, lastName, identityType, identityValue, dateOfBirth, language } = identity

  return {
    edit: false,
    firstName,
    otherName,
    lastName,
    identityType,
    identityValue,
    dateOfBirth,
    language,
    saveInProgress: false
  }
}


class EmployeeIdentityDetails extends React.PureComponent {
  static propTypes = {
    identity: PropTypes.object.isRequired
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.data.identity === undefined && this.props.data.identity) {
      this.setState(initialiseState(this.props.data.identity))
    }
  }

  state = this.props.data.identity
    ? initialiseState(this.props.data.identity)
    : {}

  identityFieldChanged = () => {
    const { firstName, otherName, lastName, identityType, identityValue, dateOfBirth, language } = this.props.data.identity

    return (
      firstName !== this.state.firstName ||
      otherName !== this.state.otherName ||
      lastName !== this.state.lastName ||
      dateOfBirth !== this.state.dateOfBirth ||
      (this.state.identityType && identityType !== this.state.identityType.toUpperCase()) ||
      identityValue !== this.state.identityValue ||
      (this.state.language && language !== this.state.language.toUpperCase())
    )
  }

  handleSave = () => {
    this.setState({ saveInProgress: true })

    let identity = this.props.data.identity
    let input = {
      id: identity.id
    }

    let CoreIDFields = {}

    if (identity.firstName !== this.state.firstName) {
      input.firstName = this.state.firstName
      if (COREID_ACTIVE) CoreIDFields["first_name"] = this.state.firstName
    }
    if (identity.otherName !== this.state.otherName) {
      input.otherName = this.state.otherName
      if (COREID_ACTIVE) CoreIDFields["other_name"] = this.state.otherName
    }
    if (identity.lastName !== this.state.lastName) {
      input.lastName = this.state.lastName
      if (COREID_ACTIVE) CoreIDFields["last_name"] = this.state.lastName
    }
    if (identity.identityType !== this.state.identityType) {
      input.identityType = this.state.identityType
    }
    if (identity.identityValue !== this.state.identityValue) {
      input.identityValue = this.state.identityValue
      if (COREID_ACTIVE) CoreIDFields["identity_id"] = this.state.identityValue
    }
    if (identity.dateOfBirth !== this.state.dateOfBirth) {
      input.dateOfBirth = this.state.dateOfBirth.format('YYYY-MM-DD')
    }
    if (identity.language !== this.state.language) {
      input.language = this.state.language
    }

    this.props.saveIdentity({ variables: { input } })
      .then(() => {
        this.setState({ edit: false, saveInProgress: false })
        if (COREID_ACTIVE) notifyCoreID(CoreIDFields, identity.identityValue)
      })
      .catch(error => {
        this.setState({ edit: false, saveInProgress: false })
        Sentry.captureMessage('Error saving identity details.')
      })
  }

  toggleEdit = () => this.setState(prevState => ({ edit: !prevState.edit }))

  handleDetailChange = (e) => {
    const target = e.target
    this.setState(
      { [target.id]: target.value },
      () => {
        if (target.value.length === 13 &&
            this.state.identityType === 'ZA_ID' &&
            this.isValidIdentityValue('ZA_ID', target.value)) {

          const dob = getDateOfBirthFromSouthAfricanID(this.state.identityValue)
          if (this.state.dateOfBirth !== dob) {
            this.setState({ dateOfBirth: dob})
          }
        }
      }
    )
  }

  handleBirthDateChange = (date) => this.setState({ dateOfBirth: date })

  isValidIdentityValue = (identityType, identityValue) => (
    identityType && identityValue && identityType.toLowerCase() === 'za_id'
      ? isValidSouthAfricanID(identityValue)
      : identityValue && identityValue.length > 0  // too many variations to properly validate passport numbers
  )

  render() {
    if (this.props.data.loading) {
      return <Spinner className='slds-is-relative' />
    }

    if (this.props.data.error) {
      return <Warning>Failed to fetch employee details</Warning>
    }

    const { firstName, otherName, lastName, identityType, identityValue, dateOfBirth, language, verified } = this.props.data.identity

    return (
      <InfoDisplay
        title='Personal'
        rows={
          <tbody>
            <tr>
              <th className='slds-size_1-of-4 slds-theme_shade slds-text-heading_label'>
                Name
              </th>
              <td>
                {this.state.edit
                  ? <div className='slds-grid'>
                      <Input
                        id='firstName'
                        placeholder='First name'
                        value={this.state.firstName || ''}
                        onChange={this.handleDetailChange}
                      />
                      <Input
                        id='otherName'
                        placeholder='Other name'
                        value={this.state.otherName || ''}
                        onChange={this.handleDetailChange}
                        className='slds-m-left_x-small'
                      />
                      <Input
                        id='lastName'
                        placeholder='Last name'
                        value={this.state.lastName || ''}
                        onChange={this.handleDetailChange}
                        className='slds-m-left_x-small'
                      />
                    </div>
                  : `${firstName}${otherName ? ` (${otherName})` : ''} ${lastName}`
                }
              </td>
            </tr>
            <tr>
              <th className='slds-size_1-of-4 slds-theme_shade slds-text-heading_label'>
                Identitification
              </th>
              <td>
                {this.state.edit
                  ? <div className='slds-grid'>
                      <Input
                        id='identityValue'
                        placeholder='Identity number'
                        value={this.state.identityValue || ''}
                        onChange={this.handleDetailChange}
                        hasError={!this.isValidIdentityValue(this.state.identityType, this.state.identityValue)}
                      />
                      <SelectWithData
                        id='identityType'
                        forField='IdentityIdentityType'
                        value={this.state.identityType}
                        onChange={this.handleDetailChange}
                        hasError={this.state.identityValue && !this.state.identityType}
                        noDefaultOption={false}
                        className='slds-m-left_x-small'
                      />
                    </div>
                  : <div className='slds-grid'>
                      {identityValue}
                      <div className='slds-m-left_x-small'>
                        {identityType
                          ? <SpanWithData forField='IdentityIdentityType' searchKey={identityType} />
                          : 'not specified'
                        }
                      </div>
                      {verified &&
                        <div className='slds-col_bump-left'>
                          <div className='slds-text-align_right' style={{ color: 'green' }}>
                            <SvgIcon
                              variant='utility'
                              symbol='check'
                              size='x-small'
                              colour='green'
                              className='slds-m-left_x-small slds-m-right_xx-small'
                            />
                            Verified
                          </div>
                        </div>
                      }
                    </div>
                }
              </td>
            </tr>
            <tr>
              <th className='slds-size_1-of-4 slds-theme_shade slds-text-heading_label'>
                Date of birth
              </th>
              <td>
                {this.state.edit && identityType === 'PASSPORT'
                  ? <DatePicker
                      selected={this.state.dateOfBirth
                        ? moment(this.state.dateOfBirth)
                        : null
                      }
                      onChange={this.handleBirthDateChange}
                      showMonthDropdown={true}
                      showYearDropdown={true}
                      maxDate={moment()}
                      dateFormat="D MMM YYYY"
                      className='slds-input'
                    />
                  : dateOfBirth
                    ? moment(this.state.dateOfBirth, 'YYYY-MM-DD').format('D MMMM YYYY')
                    : '-'
                }
              </td>
            </tr>
            <tr>
              <th className='slds-size_1-of-4 slds-theme_shade slds-text-heading_label'>
                Preferred language
              </th>
              <td>
                {this.state.edit
                  ? <SelectWithData
                      id='language'
                      placeholder='Language'
                      forField='IdentityLanguage'
                      value={this.state.language}
                      onChange={this.handleDetailChange}
                      noDefaultOption={true}
                    />
                  : <SpanWithData
                      forField='IdentityLanguage'
                      searchKey={language}
                    />
                }
              </td>
            </tr>
          </tbody>
        }
        edit={this.state.edit}
        onCancelClick={this.toggleEdit}
        onSaveClick={this.handleSave}
        onEditClick={this.toggleEdit}
        saveDisabled={!this.identityFieldChanged() || this.state.saveInProgress}
        saveButtonLabel={this.state.saveInProgress ? 'Saving...' : null}
        noFixedLayout={true}
        titleBold={false}
        className='slds-m-bottom_x-large'
      />
    )
  }
}

const getIdentity = gql`
  query($id: ID!) {
    identity(id: $id) {
      id
      dateOfBirth
      firstName
      identityType
      identityValue
      lastName
      language
      otherName
      verified
    }
  }
`

const saveIdentity = gql`
  mutation ($input: IdentityMutationInput!) {
    identityMutate(input: $input) {
      identity {
        id
        dateOfBirth
        firstName
        otherName
        lastName
        identityType
        identityValue
        language
        verified
      }
    }
  }
`

const withData =
  graphql(getIdentity, {
    options: ({ identityId }) => ({
      variables: {
        id: identityId
      }
    })
  })

const withIdentityMutate =
  graphql(saveIdentity, {
    name: 'saveIdentity'
  })

export default compose(
  withData,
  withIdentityMutate
)(EmployeeIdentityDetails)



// WEBPACK FOOTER //
// ./src/components/messageplus/employee-identity-details/index.js